/** @rpg/vfx: VFX on three.quarks (spike 002; loaded in phase A1, built in phase B; card engine/docs/composer/cards/vfx.md). */
// One three.quarks BatchedRenderer per scene, emitters built from a small data description, added, updated and
// removed (phase A1's fit check, decision 0047); a burst at a point aimed along a direction that removes itself when
// done (phase B). The emitter data is ours, not three.quarks', so a backend swap changes no game code (0047).
// Phase B adds, beside it: the impact cue router (impact.ts), hit flashes (flash.ts), telegraphs projected on the
// ground (telegraph.ts) and weapon trails (trail.ts). Weather comes in phase C.
import * as THREE from "three";
import {
  BatchedRenderer,
  ColorOverLife,
  ConeEmitter,
  ConstantColor,
  ConstantValue,
  Gradient,
  IntervalValue,
  ParticleSystem,
  PointEmitter,
  RenderMode,
  SizeOverLife,
  PiecewiseBezier,
  Bezier,
  SphereEmitter,
  ApplyForce,
  Vector3 as QVector3,
  Vector4,
  type EmitterShape,
} from "three.quarks";

export const VFX_VERSION = "0.2.0" as const;

export { ImpactRouter, type CueTable, type ChannelCue, type CueSink, type CueCounts, type ImpactRouterOptions } from "./impact";
export { Flashes, createFlashShell, flashMaterial, FLASH_SHELL_NAME, FLASH_FAULTS, type FlashShell } from "./flash";
export { Telegraphs, type TelegraphShape, type TelegraphStyle, type TelegraphSpec, type TelegraphState, type TelegraphOptions } from "./telegraph";
export { Trails, type TrailOptions, type WeaponSample } from "./trail";
export { cameraWeather, WEATHER_FAULTS, type CameraWeather, type CameraWeatherSpec, type WeatherSettle } from "./weather";

/** RGBA, 0..1. */
export type Rgba = readonly [number, number, number, number];

/** An emitter as data. Numbers are SI (metres, seconds); a `[min, max]` pair is a random range per particle. */
export interface EmitterSpec {
  /** Where particles start. */
  shape: { type: "point" } | { type: "sphere"; radius: number } | { type: "cone"; radius: number; angleDeg: number } | { type: "box"; width: number; height: number };
  /** Particles per second while emitting. */
  rate: number;
  /** One-off bursts: `count` particles at `time` seconds into each cycle. */
  bursts?: readonly { time: number; count: number }[];
  /** Seconds a particle lives. */
  life: number | readonly [number, number];
  /** Start speed, m/s, along the shape's direction. */
  speed: number | readonly [number, number];
  /** Start size, metres. */
  size: number | readonly [number, number];
  /** Start colour, or a gradient over life (`from` → `to`). */
  color: Rgba | { from: Rgba; to: Rgba };
  /** Size multiplier over life, start → end (default 1 → 1). */
  sizeOverLife?: readonly [number, number];
  /** A constant acceleration, m/s² (e.g. gravity `[0, -9.8, 0]`). */
  force?: readonly [number, number, number];
  /** The emission cycle, seconds (default 1). */
  duration?: number;
  /** Repeat the cycle (default true). */
  looping?: boolean;
  /** Additive (sparks, glows) or normal (dust, smoke) blending. Default additive. */
  blending?: "additive" | "normal";
  /** Particles simulate in world space, so a moving emitter leaves a trail. Default true. */
  worldSpace?: boolean;
  /** Camera-facing sprites (default), or sprites stretched along their velocity (rain): a sprite is `size` wide and
   *  (speed × |velocity| + length) × `size` long (three.quarks' speedFactor, default 1, and lengthFactor). */
  render?: { type: "billboard" } | { type: "stretched"; length: number; speed?: number };
}

/** A live emitter. */
export interface Emitter {
  readonly id: number;
  /** The emitter's object: position, rotate or parent it. */
  readonly object: THREE.Object3D;
  /** The underlying three.quarks system (for what the spec can't say yet). */
  readonly system: ParticleSystem;
  /** Stop emitting; live particles finish their lives. */
  stop(): void;
}

/** One scene's VFX: add `root` to the scene and call `update(dt)` once per frame. */
export interface VfxSystem {
  /** The batched renderer: add it to the scene once. */
  readonly root: THREE.Object3D;
  /** Build an emitter from its spec, at `position` under `parent` (default the root's parent, else the root), its
   *  +Z (a cone's axis) turned along `direction`. `autoRemove` removes it once a non-looping emitter has emitted and
   *  its particles have died (a one-off burst). */
  add(spec: EmitterSpec, o?: { position?: readonly [number, number, number]; direction?: readonly [number, number, number]; parent?: THREE.Object3D; autoRemove?: boolean }): Emitter;
  /** Remove an emitter and its particles at once. */
  remove(e: Emitter): void;
  /** Advance every emitter by `dt` seconds. */
  update(dt: number): void;
  /** Live emitters and particles, and the batches (draw calls) they use. */
  counts(): { emitters: number; particles: number; batches: number };
  /** Remove everything and free the shared texture and materials. */
  dispose(): void;
}

const range = (v: number | readonly [number, number]): ConstantValue | IntervalValue => (typeof v === "number" ? new ConstantValue(v) : new IntervalValue(v[0], v[1]));
const v4 = (c: Rgba): Vector4 => new Vector4(c[0], c[1], c[2], c[3]);

/** Throws on a spec that can't work (negative rates, empty lives…). */
export function validateEmitter(s: EmitterSpec): void {
  const pos = (n: number | readonly [number, number], what: string, allowZero = false): void => {
    const [a, b] = typeof n === "number" ? [n, n] : n;
    if (!(Number.isFinite(a) && Number.isFinite(b)) || a > b || (allowZero ? a < 0 : a <= 0)) throw new Error(`emitter: ${what} must be ${allowZero ? "≥ 0" : "> 0"} (got ${JSON.stringify(n)})`);
  };
  pos(s.rate, "rate", true);
  pos(s.life, "life");
  pos(s.speed, "speed", true);
  pos(s.size, "size");
  if (s.shape.type === "box" && !(s.shape.width > 0 && s.shape.height > 0)) throw new Error("emitter: a box needs a width and height > 0");
  if (s.duration !== undefined) pos(s.duration, "duration");
  if (s.rate === 0 && !s.bursts?.length) throw new Error("emitter: no rate and no bursts emits nothing");
  for (const b of s.bursts ?? []) if (!(b.count > 0 && b.time >= 0)) throw new Error(`emitter: bad burst ${JSON.stringify(b)}`);
}

/** A soft round sprite, built in code (no image file): white, alpha falling off from the centre. */
function softDot(size = 32): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const dx = (x + 0.5) / size - 0.5;
      const dy = (y + 0.5) / size - 0.5;
      const a = Math.max(0, 1 - Math.sqrt(dx * dx + dy * dy) * 2);
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = 255;
      data[i + 3] = Math.round(255 * a * a);
    }
  const t = new THREE.DataTexture(data, size, size);
  t.needsUpdate = true;
  return t;
}

/** A filled rectangle in the emitter's XY plane, emitting along its +Z (a sheet of rain over the camera). Positions
 *  are random (a view effect: particles are never sim state). */
class BoxEmitter implements EmitterShape {
  readonly type = "box";
  constructor(
    readonly width: number,
    readonly height: number,
  ) {}
  initialize(p: { position: { set(x: number, y: number, z: number): unknown }; velocity: { set(x: number, y: number, z: number): unknown }; startSpeed: number }): void {
    p.position.set((Math.random() - 0.5) * this.width, (Math.random() - 0.5) * this.height, 0);
    p.velocity.set(0, 0, p.startSpeed);
  }
  update(): void {}
  toJSON(): ReturnType<EmitterShape["toJSON"]> {
    return { type: this.type, width: this.width, height: this.height } as unknown as ReturnType<EmitterShape["toJSON"]>;
  }
  clone(): EmitterShape {
    return new BoxEmitter(this.width, this.height);
  }
}

function shapeOf(s: EmitterSpec["shape"]): EmitterShape {
  switch (s.type) {
    case "box":
      return new BoxEmitter(s.width, s.height);
    case "point":
      return new PointEmitter();
    case "sphere":
      return new SphereEmitter({ radius: s.radius });
    case "cone":
      return new ConeEmitter({ radius: s.radius, angle: (s.angleDeg * Math.PI) / 180 });
  }
}

/** Create one scene's VFX system. */
export function createVfx(): VfxSystem {
  const batch = new BatchedRenderer();
  batch.name = "vfx";
  const texture = softDot();
  const materials = {
    additive: new THREE.MeshBasicMaterial({ map: texture, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }),
    normal: new THREE.MeshBasicMaterial({ map: texture, transparent: true, blending: THREE.NormalBlending, depthWrite: false }),
  };
  const live = new Map<number, Emitter>();
  // one-off bursts: removed once their cycle and the longest life have passed with no particle left
  const auto = new Map<number, { age: number; until: number }>();
  let next = 1;

  const remove = (e: Emitter): void => {
    if (!live.delete(e.id)) return;
    auto.delete(e.id);
    batch.deleteSystem(e.system);
    e.object.removeFromParent();
    e.system.dispose();
  };

  return {
    root: batch,
    add(spec, o = {}) {
      validateEmitter(spec);
      const behaviors = [];
      if (!Array.isArray(spec.color) && "from" in spec.color) behaviors.push(new ColorOverLife(new Gradient([[new QVector3(spec.color.from[0], spec.color.from[1], spec.color.from[2]), 0], [new QVector3(spec.color.to[0], spec.color.to[1], spec.color.to[2]), 1]], [[spec.color.from[3], 0], [spec.color.to[3], 1]])));
      if (spec.sizeOverLife) behaviors.push(new SizeOverLife(new PiecewiseBezier([[new Bezier(spec.sizeOverLife[0], (2 * spec.sizeOverLife[0] + spec.sizeOverLife[1]) / 3, (spec.sizeOverLife[0] + 2 * spec.sizeOverLife[1]) / 3, spec.sizeOverLife[1]), 0]])));
      if (spec.force) {
        const [x, y, z] = spec.force;
        const mag = Math.hypot(x, y, z);
        if (mag > 0) behaviors.push(new ApplyForce(new QVector3(x / mag, y / mag, z / mag), new ConstantValue(mag)));
      }
      const start = Array.isArray(spec.color) ? (spec.color as Rgba) : (spec.color as { from: Rgba }).from;
      const system = new ParticleSystem({
        duration: spec.duration ?? 1,
        looping: spec.looping ?? true,
        shape: shapeOf(spec.shape),
        startLife: range(spec.life),
        startSpeed: range(spec.speed),
        startSize: range(spec.size),
        startColor: new ConstantColor(v4(start)),
        emissionOverTime: new ConstantValue(spec.rate),
        emissionBursts: (spec.bursts ?? []).map((b) => ({ time: b.time, count: new ConstantValue(b.count), cycle: 1, interval: 0.01, probability: 1 })),
        material: materials[spec.blending ?? "additive"],
        ...(spec.render?.type === "stretched"
          ? { renderMode: RenderMode.StretchedBillBoard, rendererEmitterSettings: { speedFactor: spec.render.speed ?? 1, lengthFactor: spec.render.length } }
          : { renderMode: RenderMode.BillBoard }),
        worldSpace: spec.worldSpace ?? true,
        behaviors,
      });
      const object = system.emitter;
      if (o.position) object.position.set(o.position[0], o.position[1], o.position[2]);
      if (o.direction) {
        const d = new THREE.Vector3(o.direction[0], o.direction[1], o.direction[2]);
        if (d.lengthSq() > 1e-12) object.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), d.normalize());
      }
      (o.parent ?? batch.parent ?? batch).add(object);
      batch.addSystem(system);
      const e: Emitter = { id: next++, object, system, stop: () => system.endEmit() };
      live.set(e.id, e);
      if (o.autoRemove) {
        const maxLife = typeof spec.life === "number" ? spec.life : spec.life[1];
        auto.set(e.id, { age: 0, until: (spec.looping === false ? (spec.duration ?? 1) : Infinity) + maxLife });
      }
      return e;
    },
    remove,
    update(dt) {
      batch.update(dt);
      for (const [id, a] of auto) {
        a.age += Math.max(0, dt);
        const e = live.get(id);
        if (e && a.age >= a.until && e.system.particleNum === 0) remove(e);
      }
    },
    counts() {
      let particles = 0;
      for (const e of live.values()) particles += e.system.particleNum;
      // three.quarks keeps an emptied batch for reuse; count the ones in use
      return { emitters: live.size, particles, batches: batch.batches.filter((b) => b.systems.size > 0).length };
    },
    dispose() {
      for (const e of [...live.values()]) remove(e);
      texture.dispose();
      materials.additive.dispose();
      materials.normal.dispose();
      batch.removeFromParent();
    },
  };
}
